import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api, { apiErr } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { KINDS, SCORE_TYPES, TIERS, TIER_COLOR, scoreLabel, blankSection } from "@/lib/perform";
import { Hand, MessageCircle, Trophy, Clock, Plus, Trash2, ChevronUp, ChevronDown, Star, Lock, Send } from "lucide-react";

const field = "h-10 rounded-lg bg-[#0B0D11] border border-[#1E222A] px-3 text-sm w-full focus:outline-none focus:border-acid/60";
const area = "rounded-lg bg-[#0B0D11] border border-[#1E222A] px-3 py-2 text-sm w-full focus:outline-none focus:border-acid/60";

export function KindPill({ kind }) {
  const k = KINDS[kind] || KINDS.metcon;
  return (
    <span className="mono-label rounded-full px-2 py-0.5 border" style={{ color: k.c, borderColor: `${k.c}55`, background: `${k.c}12` }}>
      {k.label}
    </span>
  );
}

export function SectionBlock({ s, index, myScore, onLog, canLog }) {
  const k = KINDS[s.kind] || KINDS.metcon;
  return (
    <div className="relative rounded-xl border border-[#1E222A] bg-[#0B0D11] overflow-hidden" data-testid={`section-${index}`}>
      <div className="absolute left-0 top-0 bottom-0 w-1" style={{ background: k.c }} />
      <div className="p-4 pl-5">
        <div className="flex flex-wrap items-center gap-2 justify-between">
          <div className="flex items-center gap-2 min-w-0">
            <KindPill kind={s.kind} />
            <p className="font-heading font-bold truncate">{s.title}</p>
          </div>
          <div className="flex items-center gap-2 text-xs text-[#8A91A6]">
            {s.time_cap_min ? <span className="inline-flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> cap {s.time_cap_min} min</span> : null}
            {s.score_type !== "none" && <span className="mono-label">{scoreLabel(s.score_type)}</span>}
          </div>
        </div>
        {s.description && <p className="text-sm text-[#D7DCE5] whitespace-pre-line mt-2 leading-relaxed">{s.description}</p>}
        {canLog && s.score_type !== "none" && (
          <div className="mt-3 flex flex-wrap items-center gap-2">
            {myScore ? (
              <span className="inline-flex items-center gap-2 rounded-full bg-acid/10 border border-acid/40 text-acid px-3 h-8 text-sm" data-testid={`my-score-${index}`}>
                {myScore.is_pr && <Trophy className="w-3.5 h-3.5" />} {myScore.display}
                <span className="mono-label" style={{ color: TIER_COLOR[myScore.tier] }}>{myScore.tier}</span>
              </span>
            ) : null}
            <button onClick={() => onLog(index)} data-testid={`log-score-${index}`}
              className={`rounded-full h-8 px-4 text-sm font-medium ${myScore ? "border border-[#343A4A] text-[#B7BECB] hover:border-acid" : "bg-acid text-black hover:bg-[#AACC00]"}`}>
              {myScore ? "Edit score" : "Log score"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function TimeInput({ value, onChange }) {
  return <input className={field} inputMode="numeric" placeholder="mm:ss, e.g. 4:58" value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid="score-time" />;
}

export function ScoreDialog({ open, onOpenChange, wod, section, existing, onSaved }) {
  const s = wod?.sections?.[section];
  const [f, setF] = useState({});
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!open) return;
    const e = existing || {};
    setF({ time: e.time_sec ? `${Math.floor(e.time_sec / 60)}:${String(e.time_sec % 60).padStart(2, "0")}` : "", capped: !!e.capped, cap_reps: e.cap_reps ?? "",
      rounds: e.rounds ?? "", reps: e.reps ?? "", load_kg: e.load_kg ?? "", calories: e.calories ?? "", distance_m: e.distance_m ?? "",
      tier: e.tier || "rx", notes: e.notes || "", feedback_rating: 0, feedback_text: "" });
  }, [open, existing]);
  if (!s) return null;
  const set = (k) => (v) => setF((x) => ({ ...x, [k]: v?.target ? v.target.value : v }));
  const num = (v) => (v === "" || v === null || v === undefined ? null : Number(v));
  const save = async () => {
    setBusy(true);
    try {
      const body = { section, tier: f.tier, notes: f.notes, time: f.time, capped: f.capped, cap_reps: num(f.cap_reps), rounds: num(f.rounds),
        reps: num(f.reps), load_kg: num(f.load_kg), calories: num(f.calories), distance_m: num(f.distance_m),
        feedback_rating: f.feedback_rating || null, feedback_text: f.feedback_text };
      const { data } = await api.post(`/api/wods/${wod.id}/scores`, body);
      if (data.pr) toast.success(`New PR on ${data.pr.movement}: ${data.pr.display}`, { duration: 6000 });
      else toast.success(`Logged: ${data.score.display}`);
      onSaved?.(data);
      onOpenChange(false);
    } catch (err) { toast.error(apiErr(err)); }
    setBusy(false);
  };
  const st = s.score_type;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="surface border-[#1E222A] max-w-lg">
        <DialogHeader><DialogTitle className="font-heading">Log your score: {s.title}</DialogTitle></DialogHeader>
        <div className="space-y-4" data-testid="score-dialog">
          <div className="flex flex-wrap gap-2">
            {TIERS.map((t) => (
              <button key={t.key} onClick={() => set("tier")(t.key)} data-testid={`tier-${t.key}`}
                className={`rounded-full px-4 h-9 text-sm border ${f.tier === t.key ? "bg-acid text-black border-acid" : "border-[#343A4A] text-[#B7BECB]"}`}>{t.label}</button>
            ))}
          </div>
          {st === "time" && (
            <div className="space-y-2">
              {!f.capped && <TimeInput value={f.time} onChange={set("time")} />}
              <label className="flex items-center gap-2 text-sm text-[#B7BECB]">
                <input type="checkbox" checked={!!f.capped} onChange={(e) => set("capped")(e.target.checked)} /> I hit the time cap
              </label>
              {f.capped && <input className={field} type="number" min="0" placeholder="Reps completed at the cap" value={f.cap_reps} onChange={set("cap_reps")} />}
            </div>
          )}
          {st === "amrap" && (
            <div className="grid grid-cols-2 gap-2">
              <input className={field} type="number" min="0" placeholder="Rounds" value={f.rounds} onChange={set("rounds")} data-testid="score-rounds" />
              <input className={field} type="number" min="0" placeholder="+ reps" value={f.reps} onChange={set("reps")} data-testid="score-reps" />
            </div>
          )}
          {st === "load" && <input className={field} type="number" min="0" step="0.5" placeholder="Load in kg" value={f.load_kg} onChange={set("load_kg")} data-testid="score-load" />}
          {st === "reps" && <input className={field} type="number" min="0" placeholder="Reps" value={f.reps} onChange={set("reps")} data-testid="score-reps" />}
          {st === "rounds" && <input className={field} type="number" min="0" placeholder="Rounds" value={f.rounds} onChange={set("rounds")} />}
          {st === "calories" && <input className={field} type="number" min="0" placeholder="Calories" value={f.calories} onChange={set("calories")} />}
          {st === "distance" && <input className={field} type="number" min="0" placeholder="Metres" value={f.distance_m} onChange={set("distance_m")} />}
          <textarea className={area} rows={2} placeholder="Notes: loads used, scaling, how it felt" value={f.notes} onChange={set("notes")} />
          {wod.scope === "club" && (
            <div className="rounded-lg border border-[#1E222A] p-3">
              <p className="text-sm text-[#B7BECB] mb-2">How was the session? <span className="text-[#565C6F]">(only your coaches see this)</span></p>
              <div className="flex gap-1 mb-2">
                {[1, 2, 3, 4, 5].map((n) => (
                  <button key={n} onClick={() => set("feedback_rating")(n)} aria-label={`${n} stars`}>
                    <Star className="w-6 h-6" style={{ color: n <= f.feedback_rating ? "#FFB340" : "#343A4A", fill: n <= f.feedback_rating ? "#FFB340" : "none" }} />
                  </button>
                ))}
              </div>
              <input className={field} placeholder="Anything for your coach? (optional)" value={f.feedback_text} onChange={set("feedback_text")} />
            </div>
          )}
          <button disabled={busy} onClick={save} data-testid="score-save"
            className="w-full h-11 rounded-full bg-acid text-black font-medium hover:bg-[#AACC00] disabled:opacity-60">
            {busy ? "Saving…" : "Save score"}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Comments({ scoreId }) {
  const qc = useQueryClient();
  const { data = [] } = useQuery({ queryKey: ["comments", scoreId], queryFn: async () => (await api.get(`/api/scores/${scoreId}/comments`)).data });
  const [t, setT] = useState("");
  const send = async () => {
    if (!t.trim()) return;
    try { await api.post(`/api/scores/${scoreId}/comments`, { text: t }); setT(""); qc.invalidateQueries({ queryKey: ["comments", scoreId] }); }
    catch (err) { toast.error(apiErr(err)); }
  };
  return (
    <div className="px-3 pb-3 space-y-2">
      {data.map((c) => <p key={c.id} className="text-sm"><span className="text-acid">{c.name}</span> <span className="text-[#D7DCE5]">{c.text}</span></p>)}
      <div className="flex gap-2">
        <input className={field} placeholder="Say something nice" value={t} onChange={(e) => setT(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()} />
        <button onClick={send} className="rounded-lg bg-acid text-black px-3" aria-label="Send"><Send className="w-4 h-4" /></button>
      </div>
    </div>
  );
}

export function ScoreRow({ r, onChanged, compact }) {
  const [hf, setHf] = useState({ on: r.i_high_fived, n: r.high_fives || 0 });
  const [open, setOpen] = useState(false);
  const five = async () => {
    try { const { data } = await api.post(`/api/scores/${r.id}/high-five`); setHf({ on: data.high_fived, n: data.high_fives }); onChanged?.(); }
    catch (err) { toast.error(apiErr(err)); }
  };
  const medal = r.rank === 1 ? "#FFD60A" : r.rank === 2 ? "#C0C7D4" : r.rank === 3 ? "#CD7F32" : null;
  return (
    <div className={`rounded-lg border ${r.mine ? "border-acid/50 bg-acid/5" : "border-[#1E222A] bg-[#0B0D11]"}`}>
      <div className="flex items-center gap-3 px-3 py-2">
        {r.rank && (
          <span className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-black shrink-0"
            style={{ background: medal ? `${medal}22` : "#1E222A", color: medal || "#8A91A6", border: medal ? `1px solid ${medal}` : "none" }}>{r.rank}</span>
        )}
        <div className="min-w-0 flex-1">
          <p className="text-sm truncate">{r.name}{r.mine && <span className="text-acid"> (you)</span>}
            {compact && r.wod_title && <span className="text-[#565C6F]"> · {r.section_title}</span>}</p>
          {r.notes && !compact && <p className="text-xs text-[#8A91A6] truncate">{r.notes}</p>}
        </div>
        <span className="mono-label" style={{ color: TIER_COLOR[r.tier] }}>{r.tier}</span>
        <span className="font-mono2 text-sm font-bold w-28 text-right flex items-center justify-end gap-1">{r.is_pr && <Trophy className="w-3.5 h-3.5 text-[#FFD60A]" />}{r.display}</span>
        <button onClick={five} data-testid="high-five" title="High five"
          className={`inline-flex items-center gap-1 rounded-full px-2 h-7 text-xs border ${hf.on ? "bg-[#FFB340]/15 border-[#FFB340] text-[#FFB340]" : "border-[#343A4A] text-[#8A91A6] hover:text-white"}`}>
          <Hand className="w-3.5 h-3.5" /> {hf.n || ""}
        </button>
        <button onClick={() => setOpen((o) => !o)} className="inline-flex items-center gap-1 rounded-full px-2 h-7 text-xs border border-[#343A4A] text-[#8A91A6] hover:text-white" title="Comments">
          <MessageCircle className="w-3.5 h-3.5" /> {r.comment_count || ""}
        </button>
      </div>
      <AnimatePresence>{open && <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }}><Comments scoreId={r.id} /></motion.div>}</AnimatePresence>
    </div>
  );
}

export function Whiteboard({ board, division, onChanged }) {
  const [tier, setTier] = useState("all");
  const [div, setDiv] = useState("all");
  const rows = board.scores.filter((r) => (tier === "all" || r.tier === tier || (tier === "rx" && r.tier === "rx+")) && (div === "all" || r.division === div));
  return (
    <div className="space-y-3" data-testid="whiteboard">
      <div className="flex flex-wrap items-center gap-2 justify-between">
        <p className="font-heading font-bold">{board.title}</p>
        <div className="flex flex-wrap gap-1.5">
          {["all", "rx", "scaled"].map((t) => (
            <button key={t} onClick={() => setTier(t)} className={`mono-label rounded-full px-3 h-7 border ${tier === t ? "bg-acid text-black border-acid" : "border-[#343A4A] text-[#8A91A6]"}`}>{t === "all" ? "all" : t}</button>
          ))}
          {["all", "men", "women"].map((d) => (
            <button key={d} onClick={() => setDiv(d)} className={`mono-label rounded-full px-3 h-7 border ${div === d ? "bg-[#3D7BFF] text-black border-[#3D7BFF]" : "border-[#343A4A] text-[#8A91A6]"}`}>{d === "all" ? "everyone" : d}</button>
          ))}
        </div>
      </div>
      {rows.length === 0 ? (
        <p className="text-sm text-[#8A91A6] rounded-lg border border-dashed border-[#343A4A] p-4 text-center">No scores yet. Be the first on the board.</p>
      ) : rows.map((r, i) => <ScoreRow key={r.id} r={{ ...r, rank: i + 1 }} onChanged={onChanged} />)}
    </div>
  );
}

export function HiddenWod({ w }) {
  const t = w.publish_at ? new Date(w.publish_at) : null;
  return (
    <div className="relative overflow-hidden rounded-2xl border border-[#343A4A] bg-[#0B0D11] p-6 text-center" data-testid="hidden-wod">
      <Lock className="w-8 h-8 text-acid mx-auto" />
      <p className="font-heading font-black text-xl mt-2">{w.title}</p>
      <p className="text-sm text-[#8A91A6] mt-1">{w.message}</p>
      {t && <p className="mono-label text-acid mt-3">revealed {t.toLocaleString(undefined, { weekday: "short", hour: "2-digit", minute: "2-digit" })}</p>}
    </div>
  );
}

// ---------- editor (club programming and personal workouts)
export function WodEditor({ open, onOpenChange, initial, scope = "personal", onSaved }) {
  const { data: meta } = useQuery({ queryKey: ["perform-meta"], queryFn: async () => (await api.get("/api/perform/meta")).data, staleTime: 3600000 });
  const [w, setW] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!open) return;
    const base = initial || {};
    // default reveal: 6 pm the evening before, in the coach's own time zone
    let pub = base.publish_at ? new Date(base.publish_at) : null;
    if (!pub && scope === "club" && base.date) { const [y, m, d] = base.date.split("-").map(Number); pub = new Date(y, m - 1, d - 1, 18, 0); }
    setW({
      date: base.date, title: base.title || "", time: base.time || "", coach_notes: base.coach_notes || "",
      sections: (base.sections && base.sections.length ? base.sections : scope === "club" ? [blankSection("warmup"), blankSection("strength"), blankSection("metcon")] : [blankSection("metcon")]).map((s) => ({ ...s })),
      publish_at: pub ? new Date(pub.getTime() - pub.getTimezoneOffset() * 60000).toISOString().slice(0, 16) : "",
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initial?.id, initial?.date, scope]);
  if (!w) return null;
  const setS = (i, k, v) => setW((x) => ({ ...x, sections: x.sections.map((s, j) => (j === i ? { ...s, [k]: v } : s)) }));
  const move = (i, d) => setW((x) => {
    const a = [...x.sections]; const j = i + d; if (j < 0 || j >= a.length) return x;
    [a[i], a[j]] = [a[j], a[i]]; return { ...x, sections: a };
  });
  const pickBench = (i, key) => {
    const b = meta?.benchmarks?.find((x) => x.key === key);
    setW((x) => ({ ...x, sections: x.sections.map((s, j) => (j === i ? { ...s, benchmark_key: key || null, title: b ? b.name : s.title, description: b ? b.description : s.description, score_type: b ? b.score_type : s.score_type } : s)) }));
  };
  const save = async (status) => {
    setBusy(true);
    try {
      const body = { ...w, scope, status, publish_at: w.publish_at ? new Date(w.publish_at).toISOString() : null,
        sections: w.sections.map((s) => ({ ...s, time_cap_min: s.time_cap_min ? Number(s.time_cap_min) : null })) };
      const { data } = initial?.id ? await api.patch(`/api/wods/${initial.id}`, body) : await api.post("/api/wods", body);
      toast.success(status === "draft" ? "Saved as draft" : scope === "club" ? "Published to your club" : "Saved to your calendar");
      onSaved?.(data); onOpenChange(false);
    } catch (err) { toast.error(apiErr(err)); }
    setBusy(false);
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="surface border-[#1E222A] max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-heading">{initial?.id ? "Edit workout" : scope === "club" ? "Program a club workout" : "Plan or log a workout"}</DialogTitle></DialogHeader>
        <div className="space-y-4" data-testid="wod-editor">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
            <input className={field} type="date" value={w.date} onChange={(e) => setW({ ...w, date: e.target.value })} data-testid="wod-date" />
            <input className={`${field} md:col-span-2`} placeholder="Title, e.g. Heavy Tuesday" value={w.title} onChange={(e) => setW({ ...w, title: e.target.value })} data-testid="wod-title" />
          </div>
          {scope === "personal" && (
            <input className={field} type="time" value={w.time || ""} onChange={(e) => setW({ ...w, time: e.target.value })} />
          )}
          {w.sections.map((s, i) => (
            <div key={i} className="rounded-xl border border-[#1E222A] p-3 space-y-2" style={{ borderLeft: `3px solid ${(KINDS[s.kind] || KINDS.metcon).c}` }}>
              <div className="flex flex-wrap gap-2 items-center">
                <select className={`${field} w-36`} value={s.kind} onChange={(e) => setS(i, "kind", e.target.value)}>
                  {Object.entries(KINDS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                </select>
                <select className={`${field} flex-1 min-w-[10rem]`} value={s.benchmark_key || ""} onChange={(e) => pickBench(i, e.target.value)} data-testid={`bench-pick-${i}`}>
                  <option value="">Custom (or pick a benchmark)</option>
                  {(meta?.groups || []).map((g) => (
                    <optgroup key={g.key} label={g.label}>
                      {meta.benchmarks.filter((b) => b.group === g.key).map((b) => <option key={b.key} value={b.key}>{b.name}</option>)}
                    </optgroup>
                  ))}
                </select>
                <div className="flex gap-1 ml-auto">
                  <button onClick={() => move(i, -1)} className="p-2 text-[#8A91A6] hover:text-white" aria-label="Move up"><ChevronUp className="w-4 h-4" /></button>
                  <button onClick={() => move(i, 1)} className="p-2 text-[#8A91A6] hover:text-white" aria-label="Move down"><ChevronDown className="w-4 h-4" /></button>
                  <button onClick={() => setW({ ...w, sections: w.sections.filter((_, j) => j !== i) })} className="p-2 text-[#8A91A6] hover:text-pulseError" aria-label="Remove part"><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
              <input className={field} placeholder="Part title" value={s.title} onChange={(e) => setS(i, "title", e.target.value)} />
              <textarea className={area} rows={3} placeholder={"Movements, reps and loads, e.g.\n21-15-9\nThrusters 43/30 kg\nPull-ups"} value={s.description} onChange={(e) => setS(i, "description", e.target.value)} />
              <div className="grid grid-cols-2 gap-2">
                <select className={field} value={s.score_type} onChange={(e) => setS(i, "score_type", e.target.value)} disabled={!!s.benchmark_key}>
                  {SCORE_TYPES.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
                </select>
                <input className={field} type="number" min="0" placeholder="Time cap (min)" value={s.time_cap_min || ""} onChange={(e) => setS(i, "time_cap_min", e.target.value)} />
              </div>
            </div>
          ))}
          <div className="flex flex-wrap gap-2">
            {Object.entries(KINDS).map(([k, v]) => (
              <button key={k} onClick={() => setW({ ...w, sections: [...w.sections, blankSection(k)] })}
                className="inline-flex items-center gap-1 rounded-full border px-3 h-8 text-xs" style={{ borderColor: `${v.c}66`, color: v.c }}>
                <Plus className="w-3.5 h-3.5" /> {v.label}
              </button>
            ))}
          </div>
          {scope === "club" && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              <label className="text-xs text-[#8A91A6]">Reveal to athletes at
                <input className={`${field} mt-1`} type="datetime-local" value={w.publish_at} onChange={(e) => setW({ ...w, publish_at: e.target.value })} />
              </label>
              <label className="text-xs text-[#8A91A6]">Coach notes (athletes don't see these)
                <input className={`${field} mt-1`} value={w.coach_notes} onChange={(e) => setW({ ...w, coach_notes: e.target.value })} />
              </label>
            </div>
          )}
          <div className="flex flex-wrap gap-2 justify-end">
            {scope === "club" && <button disabled={busy} onClick={() => save("draft")} className="rounded-full h-11 px-5 border border-[#343A4A] text-sm">Save draft</button>}
            <button disabled={busy} onClick={() => save("published")} data-testid="wod-save"
              className="rounded-full h-11 px-6 bg-acid text-black font-medium hover:bg-[#AACC00] disabled:opacity-60">
              {busy ? "Saving…" : scope === "club" ? "Publish" : "Save"}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
